import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Spin, Select, DatePicker, message } from "antd"
import dayjs from "dayjs"
import { Analytics } from "../Apis/analyticsApi"
import { translations } from "../utils/Translation/staticTranslation"
import {
  PostHogLineChart,
  PostHogBarChart,
  PostHogDoughnutChart,
  PostHogStackedBarChart,
} from "../components/PostHog/PostHogCharts"
import PostHogActivity from "../components/PostHog/PostHogActivity"
import PostHogFunnel from "../components/PostHog/PostHogFunnel"

const { RangePicker } = DatePicker

// Arabic display names for known PostHog events.
const EVENT_LABELS_AR = {
  pageview: "مشاهدة صفحة",
  pageleave: "مغادرة الصفحة",
  web_vitals: "مؤشرات أداء الويب",
  user_returned: "عودة المستخدم",
}

// Format a raw event name: drop the "$" prefix, and in Arabic use the mapped
// label; otherwise replace "_"/"-" with spaces and capitalize each word.
const prettyEvent = (raw, lang) => {
  const key = String(raw || "").replace(/^\$/, "")
  if (lang === "ar" && EVENT_LABELS_AR[key]) return EVENT_LABELS_AR[key]
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// Convert a [start, end] range to a lookback `days` window (start -> today),
// since the backend filters by a number of days. Clamped to 1..365.
const rangeToDays = (range) => {
  if (!range || !range[0]) return 30
  const diff = dayjs().startOf("day").diff(dayjs(range[0]).startOf("day"), "day") + 1
  return Math.min(365, Math.max(1, diff))
}

// Compact number formatting for the KPI strip (1,234 / 12.3K / 1.2M).
const fmtNum = (n) => {
  const v = Number(n || 0)
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 10_000) return `${(v / 1_000).toFixed(1)}K`
  return v.toLocaleString()
}

// Seconds -> "4m 9s" / "48s" / "1h 3m".
const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(Number(secs || 0)))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

const Card = ({ title, subtitle, children, lang }) => (
  <div
    className="bg-FFFFFF"
    style={{
      borderRadius: "16px",
      border: "1px solid #EFEFEF",
      padding: "20px",
      height: "100%",
    }}
  >
    <div style={{ textAlign: lang === "ar" ? "right" : "left", marginBottom: 14 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#1F1F1F" }}>{title}</div>
      {subtitle ? (
        <div style={{ fontSize: 14, color: "#8C8C80", marginTop: 4 }}>{subtitle}</div>
      ) : null}
    </div>
    {children}
  </div>
)

const PostHogAnalytics = () => {
  const lang = localStorage.getItem("language") || "en"
  const t = translations[lang]?.PostHog || {}
  const isRTL = lang === "ar"

  const DAY_OPTIONS = [
    { value: 7, label: t.last7 || "Last 7 days" },
    { value: 14, label: t.last14 || "Last 14 days" },
    { value: 30, label: t.last30 || "Last 30 days" },
    { value: 90, label: t.last90 || "Last 90 days" },
    { value: "custom", label: t.custom || "Custom range" },
  ]

  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState(30) // preset number, or "custom"
  const [range, setRange] = useState(null) // [start, end] dayjs for custom
  const [data, setData] = useState(null)
  const [conversions, setConversions] = useState(null)
  const [browserFunnel, setBrowserFunnel] = useState(null)

  const fetchOverview = useCallback(async (days) => {
    setLoading(true)
    try {
      const res = await Analytics.getPostHogOverview({ days })
      setData(res?.data?.data || null)
    } catch (err) {
      const detail =
        err?.response?.data?.error || err?.message || "Failed to load analytics"
      message.error(detail)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Conversion stats (sign-in / subscription / return). Best-effort: a failure
  // here must not blank the page, so it just leaves the section empty.
  const fetchConversions = useCallback(async (days) => {
    try {
      const res = await Analytics.getPostHogConversions({ days })
      setConversions(res?.data?.data || null)
    } catch {
      setConversions(null)
    }
  }, [])

  // Pageview funnel by browser. Best-effort (empty on failure).
  const fetchFunnel = useCallback(async (days) => {
    try {
      const res = await Analytics.getPostHogFunnel({ days })
      setBrowserFunnel(res?.data?.data || null)
    } catch {
      setBrowserFunnel(null)
    }
  }, [])

  // Initial load.
  useEffect(() => {
    fetchOverview(30)
    fetchConversions(30)
    fetchFunnel(30)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Preset dropdown change -> fetch immediately (custom waits for a range pick).
  const handleModeChange = (value) => {
    setMode(value)
    if (value !== "custom") {
      setRange(null)
      fetchOverview(value)
      fetchConversions(value)
      fetchFunnel(value)
    }
  }

  // Custom range picked -> fetch immediately once both ends are chosen.
  const handleRangeChange = (value) => {
    setRange(value)
    if (value && value[0] && value[1]) {
      fetchOverview(rangeToDays(value))
      fetchConversions(rangeToDays(value))
      fetchFunnel(rangeToDays(value))
    }
  }

  // Map the API payload into the {label, value} shape the charts expect.
  const dau = useMemo(
    () => (data?.dau || []).map((d) => ({ label: d.date, value: d.users })),
    [data]
  )
  const wau = useMemo(
    () => (data?.wau || []).map((d) => ({ label: d.week, value: d.users })),
    [data]
  )
  const events = useMemo(
    () =>
      (data?.events || [])
        .slice(0, 12)
        .map((d) => ({ label: prettyEvent(d.event, lang), value: d.count })),
    [data, lang]
  )
  // Hide local/staging traffic sources (localhost, stage.*, amplify previews).
  const referrers = useMemo(() => {
    const HIDE = ["localhost", "stage.", "amplifyapp.com"]
    return (data?.referring_domains || [])
      .filter((d) => {
        const domain = String(d.domain || "").toLowerCase()
        return !HIDE.some((frag) => domain.includes(frag))
      })
      .slice(0, 12)
      .map((d) => ({ label: d.domain, value: d.views }))
  }, [data])

  // Top viewed pages (by path).
  const topPages = useMemo(
    () =>
      (data?.top_pages || [])
        .slice(0, 12)
        .map((d) => ({ label: d.path, value: d.views })),
    [data]
  )
  // Visitor geography.
  const countries = useMemo(
    () =>
      (data?.countries || [])
        .slice(0, 12)
        .map((d) => ({ label: d.country, value: d.users })),
    [data]
  )
  // Browsers used by visitors.
  const browsers = useMemo(
    () =>
      (data?.browsers || [])
        .slice(0, 12)
        .map((d) => ({ label: d.browser, value: d.users })),
    [data]
  )
  // Device type split (desktop / mobile / tablet).
  const deviceTypes = useMemo(() => {
    const deviceLabel = (device) => {
      switch (String(device).toLowerCase()) {
        case "desktop":
          return t.deviceDesktop || "Desktop"
        case "mobile":
          return t.deviceMobile || "Mobile"
        case "tablet":
          return t.deviceTablet || "Tablet"
        default:
          return device
      }
    }
    return (data?.device_types || []).map((d) => ({
      label: deviceLabel(d.device),
      value: d.users,
    }))
  }, [data, t])
  // How many pages users view in a session (1, 2, 3+...).
  const funnel = useMemo(
    () =>
      (data?.pageview_funnel || []).map((d) => ({
        label:
          lang === "ar"
            ? `${d.pageviews} ${d.pageviews === 1 ? "صفحة" : "صفحات"}`
            : `${d.pageviews} ${d.pageviews === 1 ? "page" : "pages"}`,
        value: d.users,
      })),
    [data, lang]
  )
  // Operating systems used by visitors.
  const operatingSystems = useMemo(
    () =>
      (data?.operating_systems || [])
        .slice(0, 12)
        .map((d) => ({ label: d.os, value: d.users })),
    [data]
  )
  // Landing pages (where sessions begin).
  const entryPages = useMemo(
    () =>
      (data?.entry_pages || [])
        .slice(0, 12)
        .map((d) => ({ label: d.path, value: d.entries })),
    [data]
  )
  // New vs Returning per day: "new" comes straight from the API; "returning"
  // is that day's total active users (DAU) minus the new ones.
  const newReturning = useMemo(() => {
    const newByDate = {}
    ;(data?.new_users || []).forEach((d) => {
      newByDate[d.date] = d.users
    })
    const days = data?.dau || []
    const labels = days.map((d) => d.date)
    const newArr = days.map((d) => Math.min(newByDate[d.date] || 0, d.users))
    const returningArr = days.map((d) =>
      Math.max(0, d.users - Math.min(newByDate[d.date] || 0, d.users))
    )
    return { labels, newArr, returningArr }
  }, [data])
  // Summary KPI cards. Plain map (cheap) — no useMemo, so `t` in the labels
  // doesn't trip the exhaustive-deps lint.
  const kpiData = data?.kpis || {}
  const sessionCount = Number(kpiData.sessions || 0)
  const pagesPerSession = sessionCount
    ? (Number(kpiData.pageviews || 0) / sessionCount).toFixed(1)
    : "0"
  const deviceTotal = (data?.device_types || []).reduce((a, d) => a + (d.users || 0), 0)
  const mobileUsers =
    (data?.device_types || []).find(
      (d) => String(d.device).toLowerCase() === "mobile"
    )?.users || 0
  const mobilePct = deviceTotal ? Math.round((100 * mobileUsers) / deviceTotal) : 0
  const kpis = [
    { key: "visitors", label: t.kpiVisitors || "Unique Visitors", value: fmtNum(kpiData.unique_visitors) },
    { key: "pageviews", label: t.kpiPageviews || "Pageviews", value: fmtNum(kpiData.pageviews) },
    { key: "events", label: t.kpiEvents || "Total Events", value: fmtNum(kpiData.total_events) },
    { key: "sessions", label: t.kpiSessions || "Sessions", value: fmtNum(kpiData.sessions) },
    { key: "avg", label: t.kpiAvgSession || "Avg. Session", value: fmtDuration(kpiData.avg_session_sec) },
    { key: "bounce", label: t.kpiBounce || "Bounce Rate", value: `${Number(kpiData.bounce_rate || 0)}%` },
    { key: "pps", label: t.kpiPagesPerSession || "Pages / Session", value: pagesPerSession },
    { key: "mobile", label: t.kpiMobile || "% Mobile", value: `${mobilePct}%` },
  ]

  // Conversion-rate cards (percentage + raw counts).
  const CONV_META = {
    signin_visitor: {
      label: t.convSignin || "Sign-in Rate",
      sub: t.convSigninSub || "Sign-ins vs Visitors",
      color: "#7C3AED",
    },
    subscriber_signin: {
      label: t.convSub || "Subscription Rate",
      sub: t.convSubSub || "Subscribers vs Sign-ins",
      color: "#10B981",
    },
    return_signin: {
      label: t.convReturn || "Return Rate",
      sub: t.convReturnSub || "Return customers vs Sign-ins",
      color: "#F59E0B",
    },
  }
  const convCards = (conversions?.conversions || []).map((c) => ({
    ...c,
    ...(CONV_META[c.key] || { label: c.key, sub: "", color: "#2563EB" }),
  }))
  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      {/* Header + filters */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between"
        style={{ gap: 12, marginBottom: 20 }}
      >
        <div style={{ textAlign: isRTL ? "right" : "left" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#1F1F1F" }}>
            {t.title || "PostHog Analytics"}
          </h2>
          <div style={{ fontSize: 13, color: "#8C8C80", marginTop: 2 }}>
            {t.subtitle ||
              "Product analytics pulled live from PostHog — visits, retention, traffic sources and page flow."}
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <Select
            value={mode}
            onChange={handleModeChange}
            options={DAY_OPTIONS}
            style={{ width: 160 }}
          />
          {mode === "custom" ? (
            <RangePicker
              value={range}
              onChange={handleRangeChange}
              allowClear={false}
              disabledDate={(current) => current && current > dayjs().endOf("day")}
              style={{ width: 260 }}
            />
          ) : null}
        </div>
      </div>

      {/* Summary KPI strip — appears after first load, stays during reloads */}
      {data ? (
        <div className="row g-3" style={{ marginBottom: 16 }}>
          {kpis.map((k) => (
            <div key={k.key} className="col-6 col-md-4 col-lg">
              <div
                className="bg-FFFFFF"
                style={{
                  borderRadius: 14,
                  border: "1px solid #EFEFEF",
                  padding: "16px 18px",
                  height: "100%",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                <div style={{ fontSize: 13, color: "#8C8C80", marginBottom: 6 }}>
                  {k.label}
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "#1F1F1F", lineHeight: 1.1 }}>
                  {k.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Conversion rates — percentage + raw counts */}
      {convCards.length ? (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "#1F1F1F",
              margin: "4px 0 10px",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t.convTitle || "Conversion Rates"}
          </div>
          <div className="row g-3">
            {convCards.map((c) => (
              <div key={c.key} className="col-12 col-md-4">
                <div
                  className="bg-FFFFFF"
                  style={{
                    borderRadius: 14,
                    border: "1px solid #EFEFEF",
                    padding: "18px 20px",
                    height: "100%",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  <div style={{ fontSize: 13, color: "#8C8C80", marginBottom: 8 }}>
                    {c.label}
                  </div>
                  <div
                    className="d-flex align-items-baseline"
                    style={{ gap: 10, flexWrap: "wrap" }}
                  >
                    <div style={{ fontSize: 30, fontWeight: 800, color: c.color, lineHeight: 1 }}>
                      {c.pct}%
                    </div>
                    <div style={{ fontSize: 13, color: "#8C8C80" }}>
                      {fmtNum(c.numerator)} / {fmtNum(c.denominator)}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#B0B0A8", marginTop: 6 }}>{c.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-FFFFFF"
          style={{ height: 400, borderRadius: 16 }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.dau || "Daily Active Users (DAU)"}
              subtitle={t.dauSub || "Unique users viewing a page each day"}
            >
              <PostHogLineChart data={dau} color="#7C3AED" lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.wau || "Weekly Active Users (WAU)"}
              subtitle={t.wauSub || "Unique users viewing a page each week"}
            >
              <PostHogLineChart data={wau} color="#2563EB" lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.events || "Top Events"}
              subtitle={t.eventsSub || "Most frequent events in the period"}
            >
              <PostHogBarChart data={events} color="#3B82F6" lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.referrers || "Referring Domains"}
              subtitle={t.referrersSub || "Where your pageviews come from"}
            >
              <PostHogBarChart
                data={referrers}
                color="#10B981"
                horizontal
                lang={lang}
              />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.topPages || "Top Pages"}
              subtitle={t.topPagesSub || "Most viewed pages in the period"}
            >
              <PostHogBarChart data={topPages} color="#7C3AED" horizontal lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.countries || "Top Countries"}
              subtitle={t.countriesSub || "Where your visitors are located"}
            >
              <PostHogBarChart data={countries} color="#F59E0B" horizontal lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.browsers || "Browsers"}
              subtitle={t.browsersSub || "Browsers used by your visitors"}
            >
              <PostHogBarChart data={browsers} color="#2563EB" horizontal lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.devices || "Device Types"}
              subtitle={t.devicesSub || "Desktop vs mobile vs tablet"}
            >
              <PostHogDoughnutChart data={deviceTypes} lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.funnel || "Pages per Visitor"}
              subtitle={t.funnelSub || "How many pages users view in a session"}
            >
              <PostHogBarChart data={funnel} color="#EC4899" lang={lang} />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.newReturning || "New vs Returning"}
              subtitle={t.newReturningSub || "New and returning active users each day"}
            >
              <PostHogStackedBarChart
                labels={newReturning.labels}
                series={[
                  {
                    name: t.newUsers || "New",
                    data: newReturning.newArr,
                    color: "#7C3AED",
                  },
                  {
                    name: t.returningUsers || "Returning",
                    data: newReturning.returningArr,
                    color: "#C4B5FD",
                  },
                ]}
                lang={lang}
              />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.os || "Operating Systems"}
              subtitle={t.osSub || "Operating systems used by your visitors"}
            >
              <PostHogBarChart
                data={operatingSystems}
                color="#0EA5E9"
                horizontal
                lang={lang}
              />
            </Card>
          </div>

          <div className="col-12 col-lg-6">
            <Card
              lang={lang}
              title={t.entryPages || "Landing Pages"}
              subtitle={t.entryPagesSub || "Where visitor sessions begin"}
            >
              <PostHogBarChart
                data={entryPages}
                color="#F97316"
                horizontal
                lang={lang}
              />
            </Card>
          </div>

        </div>
      )}

      {/* Pageview funnel, by browser */}
      {browserFunnel?.steps?.length ? (
        <div style={{ marginTop: 16 }}>
          <PostHogFunnel steps={browserFunnel.steps} isRTL={isRTL} t={t} />
        </div>
      ) : null}

      {/* Activity feed (Events / Sessions / Live) — independent of the range filter */}
      <div style={{ marginTop: 16 }}>
        <PostHogActivity lang={lang} t={t} isRTL={isRTL} />
      </div>
    </div>
  )
}

export default PostHogAnalytics
